// src/pages/AuditReview.js
import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../hooks/useAuth";
import LoginPage from "../components/LoginPage";
import { useParams, useNavigate } from "react-router-dom";
import { localApplicationsApi as api } from "../utils/api";
import { formatDate, formatDue, auditStatus } from "../utils/dates";
import { loadConfig } from "../utils/config";

const DEFAULT_ACCESS_LEVELS = ["Admin", "Editor", "Viewer", "Read-Only", "Billing", "Support"];
const STATUS_BADGE = { ok: "badge-ok", "due-soon": "badge-due", overdue: "badge-overdue", unknown: "badge-neutral" };
const STATUS_LABEL = { ok: "Up to date", "due-soon": "Due soon", overdue: "Overdue", unknown: "Not set" };

export default function AuditReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [system,     setSystem]     = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(false);
  const [nextDue,    setNextDue]    = useState("");
  const [notes,      setNotes]      = useState("");
  const [userRows,   setUserRows]   = useState([]);
  const [accessLevels, setAccessLevels] = useState(DEFAULT_ACCESS_LEVELS);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get(id);
      setSystem(data);
      setUserRows(
        (data.users || [])
          .filter(u => u.active)
          .map(u => ({ ...u, _newAccess: Array.isArray(u.accessLevel) ? u.accessLevel : (u.accessLevel ? [u.accessLevel] : []), _remove: false }))
      );
      // Determine access levels: system-specific → config defaults → hardcoded
      if (data.accessLevels && data.accessLevels.length > 0) {
        setAccessLevels(data.accessLevels);
      } else {
        try {
          const cfg = await loadConfig();
          setAccessLevels(cfg.defaultAccessLevels?.length ? cfg.defaultAccessLevels : DEFAULT_ACCESS_LEVELS);
        } catch {
          setAccessLevels(DEFAULT_ACCESS_LEVELS);
        }
      }
    } catch {
      navigate("/local-applications");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const setRow = (userId, patch) =>
    setUserRows(rows => rows.map(r => r.id === userId ? { ...r, ...patch } : r));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const users = userRows.map(r => ({
        id: r.id, upn: r.upn, displayName: r.displayName,
        email: r.email, accessLevel: r._newAccess,
        notes: r.notes, active: !r._remove,
      }));
      const result = await api.submitAudit(id, { users, notes });
      setNextDue(result.nextAuditDue);
      setSubmitted(true);
    } catch (err) {
      alert("Failed to submit audit: " + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const { user: authUser, loading: authLoading } = useAuth();

  // Not signed in — show the shared login page, redirecting back here after auth
  if (!authLoading && !authUser) {
    return <LoginPage redirectUri={window.location.pathname + window.location.search} />;
  }

  if (loading || authLoading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 80 }}>
      <span className="spinner" style={{ width: 32, height: 32 }} />
    </div>
  );
  if (!system) return null;

  const status       = auditStatus(system.nextAuditDue);
  const removedCount = userRows.filter(r => r._remove).length;
  const changedCount = userRows.filter(r => !r._remove && r._newAccess !== r.accessLevel).length;

  // ── Success screen ──────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div style={{ maxWidth: 540, margin: "60px auto" }}>
        <div className="card" style={{ textAlign: "center", padding: "40px 32px" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
          <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>Audit Complete</h2>
          <p style={{ color: "var(--gray-500)", marginBottom: 24 }}>
            Access review for <strong>{system.name}</strong> has been submitted and recorded.
            {removedCount > 0 && ` ${removedCount} user${removedCount !== 1 ? "s" : ""} removed.`}
            {changedCount > 0 && ` ${changedCount} access level${changedCount !== 1 ? "s" : ""} updated.`}
          </p>
          <div style={{ background: "var(--gray-50)", borderRadius: "var(--radius)", padding: "14px 20px", marginBottom: 28, display: "inline-block" }}>
            <div style={{ fontSize: 12, color: "var(--gray-500)", marginBottom: 2 }}>Next audit due</div>
            <div style={{ fontWeight: 600, fontSize: 16 }}>{formatDate(nextDue)}</div>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
            <button className="btn-secondary" onClick={() => navigate(`/local-applications/${id}`)}>View Local Application</button>
            <button className="btn-primary" onClick={() => navigate("/local-applications")}>Back to Dashboard</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Review screen ───────────────────────────────────────────────────────────
  return (
    <div style={{ maxWidth: 720, margin: "0 auto" }}>
      <button className="btn-ghost" style={{ marginBottom: 16, paddingLeft: 0, color: "var(--purple)" }}
        onClick={() => navigate(`/local-applications/${id}`)}>
        ← Back to {system.name}
      </button>

      {/* Header */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <h1 style={{ fontSize: 18, fontWeight: 600 }}>Access Review: {system.name}</h1>
              <span className={`badge ${STATUS_BADGE[status]}`}>{STATUS_LABEL[status]}</span>
            </div>
            <div style={{ fontSize: 13, color: "var(--gray-500)", display: "flex", gap: 20, flexWrap: "wrap" }}>
              <span>📅 Audit due {formatDue(system.nextAuditDue)}</span>
              {system.lastAuditDate && <span>🕐 Last reviewed {formatDate(system.lastAuditDate)}</span>}
              <span>🔁 Every {system.auditIntervalMonths} month{system.auditIntervalMonths !== 1 ? "s" : ""}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div style={{ background: "var(--purple-light)", borderRadius: "var(--radius)", padding: "12px 16px", marginBottom: 20, fontSize: 13, color: "var(--purple-dark)" }}>
        <strong>How to review:</strong> Confirm each user's access is still appropriate, change their access level if needed, or mark them for removal. Then click <strong>Submit Review</strong> — the timer resets automatically.
      </div>

      {/* Users */}
      {userRows.length === 0 ? (
        <div className="card empty-state" style={{ marginBottom: 20 }}>
          <h3>No users to review</h3>
          <p>Add users to this system first from the system detail page.</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
          {userRows.map(row => (
            <div key={row.id} className="card" style={{
              padding: "16px 20px",
              opacity: row._remove ? 0.5 : 1,
              boxShadow: `inset 3px 0 0 ${row._remove ? "var(--red)" : row._newAccess !== row.accessLevel ? "var(--orange)" : "var(--green)"}`, borderLeft: "none",
              transition: "all 0.15s",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                {/* Avatar */}
                <div style={{ width: 38, height: 38, borderRadius: "50%", background: row._remove ? "var(--gray-300)" : "var(--purple)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 600, flexShrink: 0 }}>
                  {row.displayName?.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
                </div>
                {/* Name */}
                <div style={{ flex: 1, minWidth: 140 }}>
                  <div style={{ fontWeight: 500, fontSize: 14, textDecoration: row._remove ? "line-through" : "none" }}>{row.displayName}</div>
                  <div style={{ fontSize: 12, color: "var(--gray-500)" }}>{row.upn}</div>
                </div>
                {/* Access level multi-picker */}
                <div style={{ minWidth: 200 }}>
                  <label style={{ fontSize: 11, color: "var(--gray-500)", marginBottom: 4, display: "block" }}>Access Roles</label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {accessLevels.map(l => {
                      const active = (row._newAccess || []).includes(l);
                      return (
                        <button key={l} type="button" disabled={row._remove}
                          onClick={() => {
                            const cur = row._newAccess || [];
                            setRow(row.id, { _newAccess: active ? cur.filter(x => x !== l) : [...cur, l] });
                          }}
                          style={{ padding: "3px 9px", borderRadius: 99, fontSize: 11, cursor: row._remove ? "not-allowed" : "pointer",
                            border: active ? "1.5px solid var(--purple)" : "1.5px solid var(--gray-200)",
                            background: active ? "var(--purple)" : "#fff",
                            color: active ? "#fff" : "var(--gray-500)",
                            fontWeight: active ? 600 : 400 }}>
                          {l}
                        </button>
                      );
                    })}
                  </div>
                  {JSON.stringify(row._newAccess) !== JSON.stringify(row.accessLevel) && !row._remove && (
                    <div style={{ fontSize: 11, color: "var(--orange)", marginTop: 3 }}>
                      Was: {Array.isArray(row.accessLevel) ? row.accessLevel.join(", ") : row.accessLevel}
                    </div>
                  )}
                </div>
                {/* Action buttons */}
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  {!row._remove ? (
                    <>
                      <button onClick={() => setRow(row.id, { _newAccess: row.accessLevel })} title="Confirm — no changes"
                        style={{ padding: "6px 12px", background: row._newAccess === row.accessLevel ? "var(--green-light)" : "var(--gray-100)", color: row._newAccess === row.accessLevel ? "var(--green)" : "var(--gray-700)", border: "none", borderRadius: "var(--radius)", cursor: "pointer", fontSize: 12, fontWeight: 500 }}>
                        ✓ Confirm
                      </button>
                      <button onClick={() => setRow(row.id, { _remove: true })}
                        style={{ padding: "6px 12px", background: "var(--red-light)", color: "var(--red)", border: "none", borderRadius: "var(--radius)", cursor: "pointer", fontSize: 12, fontWeight: 500 }}>
                        ✕ Remove
                      </button>
                    </>
                  ) : (
                    <button onClick={() => setRow(row.id, { _remove: false })}
                      style={{ padding: "6px 12px", background: "var(--gray-100)", color: "var(--gray-700)", border: "none", borderRadius: "var(--radius)", cursor: "pointer", fontSize: 12, fontWeight: 500 }}>
                      ↩ Undo
                    </button>
                  )}
                </div>
              </div>
              {row.notes && (
                <div style={{ marginTop: 8, fontSize: 12, color: "var(--gray-500)", paddingLeft: 52 }}>📝 {row.notes}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Summary and notes */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Review Summary</h3>
        <div style={{ display: "flex", gap: 20, marginBottom: 16, flexWrap: "wrap" }}>
          <div style={{ fontSize: 13 }}>
            <span style={{ color: "var(--green)", fontWeight: 600 }}>{userRows.filter(r => !r._remove && r._newAccess === r.accessLevel).length}</span>
            <span style={{ color: "var(--gray-500)" }}> confirmed unchanged</span>
          </div>
          <div style={{ fontSize: 13 }}>
            <span style={{ color: "var(--orange)", fontWeight: 600 }}>{changedCount}</span>
            <span style={{ color: "var(--gray-500)" }}> access level changes</span>
          </div>
          <div style={{ fontSize: 13 }}>
            <span style={{ color: "var(--red)", fontWeight: 600 }}>{removedCount}</span>
            <span style={{ color: "var(--gray-500)" }}> to be removed</span>
          </div>
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Audit Notes (optional)</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
            placeholder="e.g. Confirmed with department head, or notes about any changes made…" />
        </div>
      </div>

      {/* Submit */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <button className="btn-secondary" onClick={() => navigate(`/local-applications/${id}`)}>Cancel</button>
        <button className="btn-primary" style={{ padding: "10px 24px", fontSize: 15 }}
          onClick={handleSubmit} disabled={submitting || userRows.length === 0}>
          {submitting
            ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Submitting…</>
            : "Submit Review & Reset Timer"}
        </button>
      </div>
    </div>
  );
}
